import { Component, ElementRef } from '@angular/core';
import { Router } from '@angular/router';

import { DynamicRouting } from './dynamic.routing';
import { AppSettings } from './app.settings';
import { interval, Observable } from 'rxjs';

import { Select, Store } from '@ngxs/store';
import {
  Transaction,
  TransactionActions
} from './transaction-store/transaction';
import { GaClient } from '../init';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MessageService } from './core/message.service';
import { DesignationActions } from '../app/transaction-store/designation';
import { BillingInfoActions } from './transaction-store/billing-info';
import { Sdk } from './core/sdk.service';

@Component({
  selector: 'app-root',
  template: `
    <form id="root" autocomplete="on">
      <router-outlet></router-outlet>
      <router-outlet name="login"></router-outlet>
      <router-outlet name="1"></router-outlet>
      <router-outlet name="2"></router-outlet>
      <router-outlet name="3"></router-outlet>
      <router-outlet name="4"></router-outlet>
      <router-outlet name="5"></router-outlet>
      <router-outlet name="6"></router-outlet>
      <router-outlet name="7"></router-outlet>
      <router-outlet name="8"></router-outlet>
      <router-outlet name="9"></router-outlet>
      <router-outlet name="10"></router-outlet>
      <div class="container" *ngIf="router.url !== '/thank-you'">
        <p
          class="error-block text-center"
          *ngIf="error.changingThisBreaksApplicationSecurity"
          [innerHtml]="error"
        ></p>
        <div class="row mb-2" id="captcha-challenge-wrapper">
          <div class="mx-auto d-inline-block" id="captcha-challenge"></div>
        </div>
        <div class="row continue border-primary primary-color-border-half">
          <router-outlet name="impact"></router-outlet>
          <router-outlet name="continue"></router-outlet>
        </div>
        <div class="row">
          <router-outlet name="testimonial"></router-outlet>
        </div>
      </div>
      <router-outlet name="footer"></router-outlet>
    </form>
  `
})
export class AppComponent {
  error: SafeHtml = '';
  @Select(state => state) state: Observable<any>;
  @Select() transaction$: Observable<Transaction>;

  constructor(
    private dynamicRouting: DynamicRouting,
    private sanitizer: DomSanitizer,
    public router: Router,
    private settings: AppSettings,
    private element: ElementRef,
    private sdk: Sdk,
    messages: MessageService,
    private store: Store
  ) {
    const properties = this.settings.getValue('properties');

    if (properties.campaign) {
      sdk.trackEmbedVisit(
        this.settings.getValue('id'),
        this.settings.getValue('organization_id'),
        properties.campaign.id,
        this.settings.getValue('session_id')
      );
    }
    GaClient.trackPageHit();

    this.router.resetConfig(this.dynamicRouting.getRouteConfig());

    const userData = this.settings.getValue('properties').user_data || {};

    // pass userData directly into the state - this could be cleaner
    if (Object.keys(userData).length) {
      this.store.dispatch(
        new BillingInfoActions.UpdateBillingInfo(
          {
            address1: userData.address1,
            address2: userData.address2,
            country: userData.country,
            middleName: userData.middle_name || undefined,
            company: userData.company_name || undefined,
            city: userData.city,
            state: userData.state,
            zip: userData.zip_code,
            firstName: userData.first_name,
            lastName: userData.last_name,
            email: userData.email,
            phone: userData.phone,
            title: userData.title || userData.salutation
          }
        )
      );
    }

    interval(1000).subscribe(() => this.setHeight());
    this.setTheme();
    this.setCustomCSS();
    this.setDefaultDesignation();
    this.transaction$.subscribe(
      item => (this.error = this.sanitizer.bypassSecurityTrustHtml(item.error))
    );
    this.preventZoom();

    // tell MessageService to start listening for postMessages
    messages.listen();
  }
  setDefaultDesignation() {
    /* some orgs are trying to exclude the designation component entirely, so
    initialize designation state with the default in case the org doesn't include
    designations as part of their embed display */
    // SetDesignations expects an array
    let defaultDesignations = [];
    const defaultDesignation = this.settings.getValue('properties').designation;

    // TODO: Extract default designation setting from designation component so we don't
    // double dip setting designations, skip for now if designations show up on the primary embed page.
    const designationOnPrimary = this.settings
      .getValue('properties')
      .components.includes('designations');

    if (defaultDesignation && !designationOnPrimary) {
      const defaultAmount = parseFloat(this.settings.getValue('properties').cash_default);


      // DEV-2804 - It seems like defaultDesignation could sometimes be an object or a string
      // which is why it loses the id and the title along the way

      // As a short way of checking we're handling an object, I'll just check if it has an ID
      // Because without an ID, posting to GMSNext won't work anyways.
      if (defaultDesignation.id) {
        defaultDesignations.push({
          id: defaultDesignation.id,
          amount: defaultAmount,
          title: defaultDesignation.title
        });
      }

      if (!defaultDesignations.length) {
        defaultDesignations = this.settings.getValue('properties').designations;
        // Conform the designations data just in case
        defaultDesignations = defaultDesignations.map(des => ({
          id: des.id,
          amount: des.amount,
        }));
      }

      this.store.dispatch(
        new DesignationActions.SetDesignations(defaultDesignations)
      );

    }
  }

  setTheme() {
    this.element.nativeElement.style.setProperty(
      '--primary',
      this.settings.getValue('properties').primary_accent
    );
    document
      .querySelector('body')
      .style.setProperty(
        '--primary',
        this.settings.getValue('properties').primary_accent
      );
  }

  setHeight() {
    let newHeight;
    const modalEl = document.querySelector('.modal');
    const target = document.querySelector('#root');
    const pickerEl = document.querySelector('.dropdown-menu');
    if (modalEl && modalEl.clientHeight) {
      newHeight = Math.max(
        2 * target.clientWidth * 0.05 + modalEl.clientHeight,
        target.clientHeight
      );
    } else if (pickerEl && pickerEl.clientHeight) {
      newHeight = target.clientHeight + pickerEl.clientHeight + 20;
    } else {
      newHeight = target.clientHeight + 20;
    }
    window.parent.postMessage(
      JSON.stringify({
        embed_id: this.settings.getValue('id'),
        iframeStyle: { height: `${newHeight}px`, visibility: 'visible' }
      }),
      '*'
    );
  }

  setCustomCSS() {
    const customCSS = this.settings.getValue('properties').custom_css;
    if (!customCSS) {
      return;
    }
    const head = document.head || document.getElementsByTagName('head')[0];
    const style = document.createElement('style');
    style.type = 'text/css';
    style.appendChild(document.createTextNode(customCSS));
    head.appendChild(style);
  }

  preventZoom() {
    // prevent the form to be zoomed in
    // iOS, Safari
    (function(doc) {
      const addEvent = 'addEventListener',
        type = 'gesturestart',
        qsa = 'querySelectorAll';
      let scales = [1, 1],
        meta: any = qsa in doc ? doc[qsa]('meta[name=viewport]') : {};
      function fix() {
        meta.content =
          'width=device-width,minimum-scale=' +
          scales[0] +
          ',maximum-scale=' +
          scales[1];
        doc.removeEventListener(type, fix, true);
      }
      if ((meta = meta[meta.length - 1]) && addEvent in doc) {
        fix();
        scales = [0.25, 1.6];
        doc[addEvent](type, fix, true);
      }
    })(document);
  }
}
